/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
/**
 * Dutch BPM tariff and depreciation tables.
 *
 * TODO(T20-139199): move the tables and the calculation to the backend.
 *
 * These rates are set by law and change every January, so keeping them here
 * makes updating them a release rather than configuration, and the historic
 * tariff under art. 10b needs catalogue prices the frontend does not have.
 * Until T20-139199 lands, the card at least checks the year itself: once the
 * tariff is behind, it says so and stops showing a range.
 *
 * Sources:
 * - Wet BPM 1992, art. 9 (BWBR0005806), as in force on 1 Jan 2026 - tariff brackets and diesel surcharge
 * - Uitvoeringsregeling BPM 1992, art. 8(5) (BWBR0005813) - flat-rate depreciation table
 */
import {parseDate} from 'shared/web';

export const NL_BPM_TARIFF_YEAR = 2026;

/** BPM is always paid in euros, whatever currency Sourcing is set to. */
export const NL_BPM_ESTIMATE_CURRENCY = 'EUR';

export interface NlBpmTariffBracket {
  /** Lower bound of the bracket in g/km, inclusive. */
  fromCo2: number;
  /** Fixed amount for the bracket, in EUR. */
  baseAmount: number;
  /** Rate per gram above the bracket's lower bound, in EUR. */
  ratePerGram: number;
}

/** Brackets are sorted ascending; the last one has no upper bound. */
export const NL_BPM_TARIFF_BRACKETS: NlBpmTariffBracket[] = [
  {fromCo2: 0, baseAmount: 687, ratePerGram: 2},
  {fromCo2: 77, baseAmount: 841, ratePerGram: 82},
  {fromCo2: 100, baseAmount: 2727, ratePerGram: 181},
  {fromCo2: 139, baseAmount: 9786, ratePerGram: 297},
  {fromCo2: 155, baseAmount: 14538, ratePerGram: 594},
];

/** An electric car pays the first bracket's fixed amount and no CO₂ component. */
export const NL_BPM_FIXED_RATE = NL_BPM_TARIFF_BRACKETS[0].baseAmount;

/**
 * The diesel surcharge uses two different numbers, which is easy to miss:
 * it only applies above 70 g/km, but it is then charged on every gram above
 * 69 g/km. A diesel at exactly 70 g/km pays none of it.
 *
 * "Is de CO2-uitstoot van uw personenauto met een dieselmotor meer dan 70 gram
 * per kilometer? Dan betaalt u bpm volgens de tabel voor uw type auto, plus een
 * dieseltoeslag van EUR 114,83 per gram CO2-uitstoot boven de 69 gram per
 * kilometer." - Belastingdienst, bpm-tarief personenauto 2026.
 */
export const NL_BPM_DIESEL_SURCHARGE_ABOVE_CO2 = 70;
export const NL_BPM_DIESEL_SURCHARGE_BASE_CO2 = 69;
export const NL_BPM_DIESEL_SURCHARGE_PER_GRAM = 114.83;

export interface NlBpmDepreciationBracket {
  /** Lower bound of the age in whole months, inclusive. */
  fromMonths: number;
  /** Depreciation at the start of the bracket, in percent. */
  basePercent: number;
  /** Added for each started month above the bracket's lower bound. */
  percentPerMonth: number;
}

export const NL_BPM_DEPRECIATION_BRACKETS: NlBpmDepreciationBracket[] = [
  {fromMonths: 0, basePercent: 0, percentPerMonth: 12},
  {fromMonths: 1, basePercent: 12, percentPerMonth: 4},
  {fromMonths: 3, basePercent: 20, percentPerMonth: 3.5},
  {fromMonths: 5, basePercent: 27, percentPerMonth: 1.5},
  {fromMonths: 9, basePercent: 33, percentPerMonth: 1},
  {fromMonths: 18, basePercent: 42, percentPerMonth: 0.75},
  {fromMonths: 30, basePercent: 51, percentPerMonth: 0.5},
  {fromMonths: 42, basePercent: 57, percentPerMonth: 0.42},
  {fromMonths: 54, basePercent: 62, percentPerMonth: 0.42},
  {fromMonths: 66, basePercent: 67, percentPerMonth: 0.42},
  {fromMonths: 78, basePercent: 72, percentPerMonth: 0.25},
  {fromMonths: 90, basePercent: 75, percentPerMonth: 0.25},
  {fromMonths: 102, basePercent: 78, percentPerMonth: 0.25},
  {fromMonths: 114, basePercent: 81, percentPerMonth: 0.19},
];

export const NL_BPM_MAX_DEPRECIATION_PERCENT = 100;

/** Mileage below which the tax office may still assess the car as new. */
export const NL_BPM_NEW_VEHICLE_MILEAGE_LIMIT = 3000;

/**
 * Low mileage alone does not make a registered car new - a 2015 car with
 * 2 000 km is still eleven years old. Only a recent registration leaves the
 * "as new" assessment on the table. The exact window is a heuristic and needs
 * confirming with the epic owner (T20-138288).
 */
export const NL_BPM_NEW_VEHICLE_MAX_AGE_MONTHS = 12;

/**
 * WLTP has been mandatory for new cars since 1 July 2020. For earlier
 * registrations we cannot tell whether the advertised CO₂ follows WLTP or the
 * older NEDC, which typically reads a fifth to a quarter lower.
 */
export const NL_BPM_WLTP_MANDATORY_FROM = parseDate('2020-07-01');

/** Estimate tolerance shown as a range, agreed by the epic owner (T20-138288). */
export const NL_BPM_ESTIMATE_TOLERANCE = 0.1;
